import { Component, Inject, OnDestroy, computed, effect, signal } from '@angular/core'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsModel } from 'src/app/shared/models/details.model'
import { format } from 'date-fns'
import { WarningDataModel } from 'src/app/shared/models/warning-event.model'
import { DataSimulationWarningService } from 'src/app/shared/services/data-simulation-warning.service'
import { AppPaths } from 'src/app/app-routing.module'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { firstValueFrom } from 'rxjs'
import { LayerSimulationWarningService } from 'src/app/shared/layers/layer-simulation-warning.service'
import { SegmentSimWarningDataModel } from 'src/app/shared/models/sim-warning-creation.model'
import { Feature, LineString } from 'geojson'
import { getTimeZoneLabel } from 'src/app/shared/utils/date.utils'
import { AppConfigModel } from 'src/app/core/models/app-config.model'
import { APP_CONFIG } from 'src/app/app.config'
import { getEmailLink } from '../../../utils/simulation-warning-details-utils'
import { MapService } from 'src/app/core/services/map.service'
import { ToastService } from 'src/app/shared/services/toast.service'
import { HttpErrorResponse } from '@angular/common/http'

const SIMULATION_LAYER_ID = `${Layers.SimWarning.name}-lines`
const RELOAD_INTERVAL_MS = 2000
const RELOAD_DURATION_MS = 30000

@Component({
  selector: 'app-simulation-warnings-details',
  templateUrl: './simulation-warnings-details.component.html',
  standalone: false,
})
export class SimulationWarningsDetailsComponent implements OnDestroy {
  public selectedFeatureId = ''
  private readonly _details = signal<Map<string, DetailsModel>>(new Map<string, DetailsModel>())
  public readonly details = this._details.asReadonly()
  public hasDetails = computed(() => this.details().size > 0)
  public rawFeature: string | undefined
  public features: MapGeoJSONFeature[] = []
  public zoomLevel: number | undefined
  public index: number = 0
  public isTestWarning: boolean = false
  public showRaw: boolean = true
  public bearing = ''
  public isLoading: boolean = false
  public refreshIntervalId: ReturnType<typeof setInterval> | undefined
  public mailToLink = ''

  constructor(
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
    private readonly mapService: MapService,
    private readonly mapEventService: MapEventsService,
    public appState: AppStateService,
    private dataService: DataSimulationWarningService,
    private simulationTestLayer: LayerSimulationWarningService,
    private toastService: ToastService,
  ) {
    this.initialize()
  }

  ngOnDestroy(): void {
    this.stopReloading()
  }

  public toggleFeature(index: number): void {
    if (index < 0 || index >= this.features.length) {
      return
    }

    this.index = index
    this.updateDetails()
  }

  public close(): void {
    this.appState.setState(AppPaths.Simulation, {
      selectedFeatureId: undefined,
    })
    this.selectedFeatureId = ''
    this._details.set(new Map<string, DetailsModel>())
    this.rawFeature = undefined
  }

  public async deleteWarning(): Promise<void> {
    if (!this.features[this.index]) {
      return
    }

    const feature = this.features[this.index] as unknown as Feature<LineString, WarningDataModel>
    const newWarning = this.createCancelledWarning(feature)

    this.isLoading = true
    try {
      await firstValueFrom(this.dataService.createTestWarnings(newWarning))
      this.handleDeleteSuccess()
    } catch (error) {
      this.handleDeleteError(error)
    } finally {
      this.isLoading = false
    }
  }

  private initialize(): void {
    this._details.set(new Map<string, DetailsModel>())
    this.setupFeatureClickSubscription()
  }

  private setupFeatureClickSubscription(): void {
    effect(() => {
      this.handleFeatureClick(this.mapEventService.clickedFeatures())
    })
  }

  private handleFeatureClick(features: MapGeoJSONFeature[]): void {
    if (!this.isValidFeatureClick(features)) {
      return
    }

    this.isTestWarning = false
    this.showRaw = true
    this._details.set(new Map<string, DetailsModel>())
    this.updateIndex(features)

    this.mapService.removeMarker()
    this.features = features
    this.updateDetails()
  }

  private isValidFeatureClick(features: MapGeoJSONFeature[]): boolean {
    if (!features?.length) {
      return false
    }

    return features[this.index]?.layer?.id === SIMULATION_LAYER_ID
  }

  private updateIndex(features: MapGeoJSONFeature[]): void {
    this.index = this.features.length !== features.length ? 0 : this.index
  }

  private updateDetails(): void {
    const currentFeature = this.features[this.index]
    if (!currentFeature) {
      return
    }

    if (this.isSelectedFeatureClickedAgain(currentFeature)) {
      this.selectedFeatureId = ''
      this.close()
      return
    }

    this.mailToLink = getEmailLink(currentFeature)
    this.selectedFeatureId = currentFeature.id?.toString() ?? ''

    const properties = currentFeature.properties as WarningDataModel
    this.rawFeature = JSON.stringify(currentFeature, undefined, 2)
    this.mapWarningsToDetails(properties)

    this.appState.setState(AppPaths.Simulation, {
      selectedFeatureId: this.selectedFeatureId,
    })
  }

  private isSelectedFeatureClickedAgain(feature: MapGeoJSONFeature): boolean {
    return !!(this.selectedFeatureId && this.selectedFeatureId === feature.id?.toString())
  }

  private mapWarningsToDetails(properties: WarningDataModel): void {
    const rawMetric = {
      title: 'Hazards',
      items: [
        {
          description: 'Global ID',
          value: this.selectedFeatureId,
        },
        {
          description: 'Warning UUID',
          value: properties.warningUUID,
        },
        {
          description: 'Segment ID',
          value: properties.segmentId,
        },
        {
          description: 'Direction',
          value: properties.direction,
        },

        {
          description: 'Type',
          value: properties.name,
        },
        {
          description: `Valid time (${getTimeZoneLabel(new Date(properties['startTime']))})`,
          value: `${
            properties['startTime']
              ? format(new Date(properties['startTime']), 'yyyy-MM-dd HH:mm')
              : '-'
          } - ${
            properties['expiryTime']
              ? format(new Date(properties['expiryTime']), 'yyyy-MM-dd HH:mm')
              : '-'
          }`,
        },
        ...(properties.confidence
          ? [
              {
                description: 'Confidence',
                value: properties.confidence.toString(),
              },
            ]
          : []),
        ...(properties.dfrs
          ? [
              {
                description: 'Dfrs',
                value: properties.dfrs,
              },
            ]
          : []),
      ],
    }

    if (properties.target) {
      rawMetric.items.push({ description: 'Target', value: properties.target })
    }

    this.setDetail('RawMetric', rawMetric)
  }

  private setDetail(key: string, value: DetailsModel): void {
    this._details.update((current) => {
      const next = new Map(current)
      next.set(key, value)
      return next
    })
  }

  private createCancelledWarning(
    feature: Feature<LineString, WarningDataModel>,
  ): SegmentSimWarningDataModel {
    const [longitude, latitude] = this.parseEventCoordinates(feature.properties.eventCoordinates)

    return {
      segmentId: feature.properties.segmentId,
      countryCode: feature.properties.countryCode,
      warningName: feature.properties.name,
      longitude,
      latitude,
      isCancelled: true,
      traceId: feature.properties.traceId,
      ttl: feature.properties.expiryTime - feature.properties.startTime,
      class: feature.properties.roadClass,
      targets: feature.properties.target ? [feature.properties.target] : [],
      bidirectional: feature.properties.bidirectional,
      direction: feature.properties.direction,
      dfrsClassification:
        feature.properties.dfrs === 'only'
          ? 'DFRS_ONLY'
          : feature.properties.dfrs === 'partly'
            ? 'DFRS_PARTLY'
            : 'DFRS_NONE',
    }
  }

  private parseEventCoordinates(eventCoordinates: string): [number, number] {
    const parsedCoordinates = JSON.parse(eventCoordinates)
    return [parsedCoordinates[0], parsedCoordinates[1]]
  }

  private handleDeleteSuccess(): void {
    this.toastService.show({ type: 'success', message: 'Warning deletion initialized' })
    this.close()
    this.startReloading()
  }

  private handleDeleteError(error?: unknown): void {
    const status = error instanceof HttpErrorResponse ? ` (${error.status})` : ''
    this.toastService.show({ type: 'error', message: `Error deleting warning${status}` })
  }

  private startReloading(): void {
    this.stopReloading()
    this.refreshIntervalId = setInterval(
      () => this.simulationTestLayer.reload(),
      RELOAD_INTERVAL_MS,
    )

    setTimeout(() => {
      this.stopReloading()
    }, RELOAD_DURATION_MS)
  }

  private stopReloading(): void {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId)
      this.refreshIntervalId = undefined
    }
  }
}
